(() => {
  'use strict';

  const loaderScript = document.currentScript;
  const mainSrc = loaderScript?.dataset.mainSrc;
  const collectionSrc = loaderScript?.dataset.collectionSrc;
  const interactiveSelector = [
    'xb-wishlist-header',
    'xb-wishlist-button',
    'xb-wishlist-button-collection',
    'xb-wishlist-float',
    '[data-xb-wishlist-trigger]',
    '.xb-wishlist__header',
    '.xb-wishlist-button__add-style',
  ].join(',');

  let state = 'idle';
  let loadPromise;

  const appendScript = ({ src, type }) =>
    new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      if (type) script.type = type;
      script.dataset.xbDeferred = 'true';
      script.onload = resolve;
      script.onerror = () => reject(new Error(`Unable to load ${src}`));
      document.head.appendChild(script);
    });

  const loadCollectionScript = async () => {
    if (document.readyState === 'loading') {
      await appendScript({ src: collectionSrc, type: 'module' });
      return;
    }

    // collection.js only registers a DOMContentLoaded callback. When it is
    // imported after that event, deliver that callback immediately.
    const nativeAddEventListener = document.addEventListener;
    document.addEventListener = function (type, listener, options) {
      if (type === 'DOMContentLoaded') {
        queueMicrotask(() => listener.call(document, new Event('DOMContentLoaded')));
        return;
      }

      return nativeAddEventListener.call(this, type, listener, options);
    };

    try {
      await appendScript({ src: collectionSrc, type: 'module' });
    } finally {
      document.addEventListener = nativeAddEventListener;
    }
  };

  const load = (reason = 'idle') => {
    if (loadPromise) return loadPromise;
    if (!mainSrc || !collectionSrc) return Promise.reject(new Error('XB Wishlist sources are missing'));

    state = 'loading';
    document.documentElement.classList.add('xb-wishlist-loading');

    loadPromise = appendScript({ src: mainSrc })
      .then(loadCollectionScript)
      .then(() => {
        state = 'ready';
        document.documentElement.classList.remove('xb-wishlist-loading');
        document.documentElement.classList.add('xb-wishlist-ready');
        document.dispatchEvent(new CustomEvent('xb-wishlist:deferred-ready', { detail: { reason } }));
      })
      .catch((error) => {
        state = 'error';
        document.documentElement.classList.remove('xb-wishlist-loading');
        document.documentElement.classList.add('xb-wishlist-error');
        console.error('[XB Wishlist] Deferred load failed:', error);
        throw error;
      });

    return loadPromise;
  };

  const closestTrigger = (target) =>
    target instanceof Element ? target.closest(interactiveSelector) : null;

  const replayInteraction = (trigger) => {
    let attempts = 0;

    const replayWhenRendered = () => {
      const actionable =
        trigger.shadowRoot?.querySelector('button, a, [role="button"]') ||
        trigger.querySelector?.('button, a, [role="button"]');

      if (actionable) {
        actionable.click();
        trigger.removeAttribute('aria-busy');
        return;
      }

      // The main bundle is evaluated before its settings request finishes.
      // Give shadow-DOM buttons time to render before replaying the first click.
      attempts += 1;
      if (attempts < 60) {
        window.setTimeout(replayWhenRendered, 50);
        return;
      }

      trigger.click?.();
      trigger.removeAttribute('aria-busy');
    };

    requestAnimationFrame(replayWhenRendered);
  };

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (state === 'idle' && closestTrigger(event.target)) load('pointerdown').catch(() => {});
    },
    { capture: true, passive: true },
  );

  document.addEventListener(
    'click',
    (event) => {
      if (state === 'ready' || state === 'error') return;

      const trigger = closestTrigger(event.target);
      if (!trigger) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      trigger.setAttribute('aria-busy', 'true');
      load('interaction').then(() => replayInteraction(trigger)).catch(() => trigger.removeAttribute('aria-busy'));
    },
    true,
  );

  const replaceWishlistShortcodes = () => {
    const pattern = /\[xb-wishlist-page([^\]]*)\]\[\/xb-wishlist-page\]/g;
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    const matches = [];
    let node;

    while ((node = walker.nextNode())) {
      if (node.nodeValue.includes('[xb-wishlist-page')) matches.push(node);
    }

    matches.forEach((textNode) => {
      const template = document.createElement('template');
      template.innerHTML = textNode.nodeValue.replace(
        pattern,
        (_match, attributes) => `<xb-wishlist-page ${attributes}></xb-wishlist-page>`,
      );
      textNode.replaceWith(template.content);
    });
  };

  const scheduleIdleLoad = () => {
    const schedule = () => {
      window.setTimeout(() => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(() => load('idle').catch(() => {}), { timeout: 4000 });
        } else {
          window.setTimeout(() => load('fallback').catch(() => {}), 1500);
        }
      }, 1000);
    };

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });
  };

  replaceWishlistShortcodes();
  scheduleIdleLoad();

  window.XBWishlistDeferred = { load, get state() { return state; } };
})();
